import * as i0 from '@angular/core';
import { Injectable } from '@angular/core';
import { from } from 'rxjs';
import { map } from 'rxjs/operators';
import { initialize } from 'launchdarkly-js-client-sdk';
import * as i2 from '@precisely/prism-ng/api';

/**
 * Stores the LaunchDarkly configuration used when initializing the LaunchDarklyModule.
 */
class LaunchDarklyConfig {
}

/**
 * @internal
 * LaunchDarkly exports it's initialization function directly.  This class wraps that function so that it
 * can be easily replaced in unit tests.
 */
class LaunchDarklyInitializer {
    constructor() {
        this.init = initialize;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LaunchDarklyInitializer, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LaunchDarklyInitializer, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LaunchDarklyInitializer, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

/**
 * Angular service that wraps LDClient.  Using this service exclusively ensures
 * that only a single LDClient instance is created per application.  It's important
 * to make LDClient a singleton as the client instance maintains internal state
 * that allows LaunchDarkly to serve feature flags without making any remote requests.
 *
 * ###  [Initialization](https://docs.launchdarkly.com/sdk/client-side/javascript#initializing-the-client)
 * In some instances, we require feature flag values before rendering the page.
 * Therefore, the service should typically be initialized in your application's root
 * module using APP_INITIALIZER.
 * See the [AppInitializerService](https://gitlab.com/preciselydata/ui-ux-globalization/design-system/design-system-starter-application/-/blob/main/client/src/app/app-initializer.service.ts)
 * in the  [starter application](https://gitlab.com/preciselydata/ui-ux-globalization/design-system/design-system-starter-application) for a detailed example.
 *
 *
 * ### User Change
 * Make a call to `identify()` whenever the current user in your application changes.
 * See the [AppInitializerService](https://gitlab.com/preciselydata/ui-ux-globalization/design-system/design-system-starter-application/-/blob/main/client/src/app/app-initializer.service.ts)
 * in the  [starter application](https://gitlab.com/preciselydata/ui-ux-globalization/design-system/design-system-starter-application) for a detailed example.
 *
 *
 * ### Close
 * When your application closes you should call `close()`.  Example:
 *
 * ```
   \@HostListener('window:beforeunload')
   closeLaunchDarkly(): void {
     this.launchDarklyService.close();
   }
 * ```
 *
 * ### CSP
 * You will need to add 'https://*.launchdarkly.com' to your CSP directive.
 */
class LaunchDarklyService {
    /**
     * @internal
     */
    constructor(initializer, window) {
        this.initializer = initializer;
        /**
         * The user to utilize when no user has logged in yet.  LaunchDarkly recommends using the
         * same user key for every initialization and then replacing that with the actual user key
         * when you know who the user is. This way LaunchDarkly counts the initialization user key
         * only once against your monthly active users, instead of every time you initialize.
         */
        this.ANONYMOUS = { key: 'prism.ng.anonymous', anonymous: true };
        window.inject(this, 'prismng', 'LaunchDarklyService');
    }
    /**
     * Returns an object based key/value map of all feature flags.
     * @returns An object map of all feature flags from LaunchDarkly.  An empty object will be returned if
     * the LaunchDarkly client has not been initialized.
     */
    all() {
        if (this.client) {
            return this.client.allFlags();
        }
        // Only log uninitialized error once.  Don't want to flood the console w/these.
        if (!this.errorLogged) {
            console.debug('Attempted to read all LaunchDarkly flags before client has been initialized');
            this.errorLogged = true;
        }
        return {};
    }
    /**
     * When your application is about to terminate, you should shut down the feature flag client.
     * This ensures that the client releases any resources it is using, and that it delivers any
     * pending analytics. If your application terminates without this shutdown step, you may not
     * see your flag evaluations and users on the dashboard, because they are derived from
     * analytics events.  Do not attempt to evaluate flags or otherwise use the client after it
     * has shut down, as doing so may result in undefined behavior. Shut down the client at a point
     * in your application's lifecycle where the client is no longer needed.
     */
    dispose() {
        return from(this.client.close());
    }
    /**
     * The feature flag service is configured to operate for one user at a time, identified or
     * anonymous. The identify feature allows customers to change the user context, such as if a
     * user logs in or changes their user settings.
     * @param user If unspecified, a static anonymous user with key 'prism.ng.anonymous' will be used.
     */
    identify(user = this.ANONYMOUS) {
        return from(this.client.identify(user)).pipe(map(() => void 0));
    }
    /**
     * Creates an instance of the LaunchDarkly client.  The client will begin attempting to connect
     * to LaunchDarkly as soon as it is created.  In typical usage, you will call initialize once at
     * startup time.
     * @param user If unspecified, a static anonymous user with key 'prism.ng.anonymous' will be used.
     */
    init(config, user = this.ANONYMOUS) {
        this.client = this.initializer.init(config.envKey, user, config.options);
        return from(this.client.waitUntilReady());
    }
    /**
     * Determines the variation of a feature flag for the current user.  This is always a fast synchronous
     * operation because all of the feature flag values for the current user have already been loaded into memory.
     * @param key
     * @param defaultValue
     * @returns The value of the feature flag from LaunchDarkly.  The default value will be returned if the flag
     * does not exist in LaunchDarkly or if the LaunchDarkly client has not been initialized.
     */
    variation(key, defaultValue) {
        if (this.client) {
            return this.client.variation(key, defaultValue);
        }
        // Only log uninitialized error once.  Don't want to flood the console w/these.
        if (!this.errorLogged) {
            console.debug('Attempted to read LaunchDarkly variation before client has been initialized');
            this.errorLogged = true;
        }
        return defaultValue;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LaunchDarklyService, deps: [{ token: LaunchDarklyInitializer }, { token: i2.WindowService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LaunchDarklyService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LaunchDarklyService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: LaunchDarklyInitializer }, { type: i2.WindowService }] });

/**
 * Generated bundle index. Do not edit.
 */

export { LaunchDarklyConfig, LaunchDarklyService };
